import { useId } from 'react';
import { Lock, Plus, Trash2 } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { SecretRefPicker } from '@/components/agent/env-vars/SecretRefPicker';
import type { AgentEnvVar } from '@/lib/types';

/**
 * One row in the blueprint "Build Secrets" editor: an env var name the
 * build will see (e.g. `NPM_TOKEN`) plus a reference to an existing
 * org-tier secret (e.g. `NPM_AUTH_TOKEN`). The server resolves
 * `secretRef` to a Runloop secret name and Runloop scopes it to the
 * build phase only.
 */
export interface BlueprintBuildSecret {
  envVar: string;
  secretRef: string;
}

interface BlueprintBuildSecretsEditorProps {
  value: BlueprintBuildSecret[];
  onChange: (next: BlueprintBuildSecret[]) => void;
}

/**
 * Editable list of `{envVar, secretRef}` pairs for the blueprint create
 * form. Uses the standard {@link SecretRefPicker}, scoped to the active
 * organization — blueprints are an org-scoped resource so personal /
 * team-tier rows are intentionally hidden from the picker.
 */
export function BlueprintBuildSecretsEditor({ value, onChange }: BlueprintBuildSecretsEditorProps) {
  function update(index: number, patch: Partial<BlueprintBuildSecret>) {
    onChange(value.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  }

  function remove(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function add() {
    onChange([...value, { envVar: '', secretRef: '' }]);
  }

  return (
    <div className="space-y-1.5">
      {value.length > 0 && (
        <ul className="space-y-1.5">
          {value.map((entry, index) => (
            <BlueprintBuildSecretRow
              key={index}
              entry={entry}
              testIdSuffix={index}
              onChange={(patch) => update(index, patch)}
              onRemove={() => remove(index)}
            />
          ))}
        </ul>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="text-xs"
        onClick={add}
        data-testid="blueprint-build-secret-add"
      >
        <Plus className="h-3 w-3" />
        Add build secret
      </Button>
    </div>
  );
}

interface RowProps {
  entry: BlueprintBuildSecret;
  testIdSuffix: number;
  onChange: (patch: Partial<BlueprintBuildSecret>) => void;
  onRemove: () => void;
}

function BlueprintBuildSecretRow({ entry, testIdSuffix, onChange, onRemove }: RowProps) {
  const inputId = useId();
  // The shared `SecretRefPicker` takes an `AgentEnvVar`. We synthesize
  // one here so the picker's selected-state machinery (label resolution,
  // selected check mark) works without forking the component. Only
  // `secretRef` flows back through `onChange`; the rest is ignored.
  const pickerEntry: AgentEnvVar = {
    key: entry.envVar,
    secretRef: entry.secretRef || undefined,
    isSecret: true,
  };
  return (
    <li
      className="flex items-center gap-1.5"
      data-testid={`blueprint-build-secret-row-${testIdSuffix}`}
    >
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-status-warning/40 bg-status-warning/10 text-status-warning"
        // `img`: a bare span may not carry `aria-label`
        // (axe `aria-prohibited-attr`); this is an icon-only glyph.
        role="img"
        aria-label="Secret"
      >
        <Lock className="h-3 w-3" />
      </span>
      <Input
        aria-label="Build secret env var name"
        id={`${inputId}-key`}
        value={entry.envVar}
        onChange={(e) => onChange({ envVar: e.target.value })}
        placeholder="NPM_TOKEN"
        className="h-7 w-0 min-w-[7rem] flex-1 font-mono text-xs"
      />
      <SecretRefPicker
        entry={pickerEntry}
        hasStoredSecrets={false}
        onChange={(patch) => {
          if (patch.secretRef !== undefined) {
            onChange({ secretRef: patch.secretRef });
          }
        }}
        owner={{ ownerType: 'organization' }}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 text-muted-foreground"
        aria-label="Remove build secret"
        onClick={onRemove}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </li>
  );
}
